<script setup lang="ts">
definePageMeta({
  name: "event",
});

const route = useRoute<"event">();

const { data: event, suspense } = useEvent(computed(() => route.params.event));

await suspense();

if (event.value) {
  if (event.value.eventType === "conferance" && event.value.website) {
    await navigateTo(event.value.website, {
      external: true,
    });
  }

  useDocumentSeo(event.value);
}

const hubspotForm = computed(() => {
  if (!event.value?.signUpForm || event.value?.fullBooked) {
    return null;
  }

  let deadline = event.value.registrationDateLimit;
  if (!deadline) {
    const now = new Date();
    deadline = [...event.value.dates].sort((a, b) => {
      const diffA = a.getTime() - now.getTime();
      const diffB = b.getTime() - now.getTime();
      return Math.abs(diffA) - Math.abs(diffB);
    })[0];
  }

  if (deadline && isFuture(deadline)) {
    return event.value.signUpForm ?? null;
  }

  return null;
});
</script>

<template>
  <div>
    <LayoutBreadCrumbs
      :background="false"
      :text-conversions="{ [route.params.event]: event?.title }"
    />
    <main
      v-if="event"
      id="main-content"
      class="mb-32 flex w-full flex-col bg-beige-10 text-green-50"
    >
      <CardsEventHero :event="event" />
      <UArticleContainer class="flex w-11/12 flex-col gap-4 text-lg sm:w-full sm:text-xl">
        <div
          v-if="event.intro"
          class="w-full text-xl font-semibold xs:text-2xl"
          v-html="event.intro"
        />

        <div v-if="event.description" class="flex flex-col gap-4 [&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:text-2xl [&_h2]:font-semibold [&_h3]:mt-8 [&_h3]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_p]:leading-8">
          <template v-for="text in event.description" :key="text">
            <UiCommonsVideoPlayer
              v-if="useCheckIfContentIsVideo(text)"
              :url="useExtractLinkFromHtml(text)"
              class="my-8 lg:my-16"
            />
            <UiCommonsImageViewer
              v-else-if="useCheckIfContentIsImage(text)"
              :image="useParseImageFromHtml(text)"
              :combo="event.eventType"
            />
            <div v-else v-html="text" />
          </template>
        </div>

        <ul
          v-if="event.guestsWithoutBio && event.guestsWithoutBio.length > 1"
          class="my-4 inline-flex flex-wrap justify-evenly gap-6 overflow-x-auto"
        >
          <li
            v-for="speaker in event.guestsWithoutBio"
            :key="speaker.name"
            class="flex flex-col"
          >
            <CrystallizeImage
              :image="speaker.img"
              alt=""
              img-class="h-40 w-full object-cover"
            />
            <span
              class="bg-green-45 p-[0.6rem] text-center text-base text-white"
              v-text="speaker.name"
            />
          </li>
        </ul>

        <ul
          v-if="event.guestsWithBio && event.guestsWithBio.length > 0"
          class="mt-14 flex w-full flex-col gap-10 [&_p]:text-lg sm:[&_p]:text-xl"
        >
          <li
            v-for="speaker in event.guestsWithBio"
            :key="speaker.name"
            class="flex flex-col xs:flex-row"
          >
            <div class="mr-4 mb-4 flex w-fit shrink-0 flex-col xs:mr-8 xs:mb-8">
              <CrystallizeImage :image="speaker.img" img-class="aspect-square h-36 object-cover" />
              <span class="bg-green-45 p-1 text-center text-xs text-white" v-text="speaker.name" />
            </div>
            <div v-html="speaker.text" />
          </li>
        </ul>

        <template v-if="event.program && event.program.length > 0">
          <h2 class="mt-8 text-2xl">
            Programmet
          </h2>
          <ul class="program mb-4 flex flex-col gap-4">
            <li
              v-for="(item, i) in event.program"
              :key="item.time ?? String(i)"
            >
              <div v-if="item.time" class="time leading-8">
                <time>{{ item.time }}</time>
                <div v-html="item.event" />
              </div>
              <div v-else v-html="item.event" />
            </li>
          </ul>
        </template>

        <UiCommonsVideoPlayer
          v-if="event.eventType !== 'webinar' && event.videoLink"
          :url="event.videoLink"
          class="my-8 w-full lg:my-6"
        />

        <UiCommonsHubspot
          v-if="hubspotForm"
          :form="hubspotForm"
          title="Meld deg på"
          :heights="{
            base: '1550px',
            small: '1460px',
            medium: '1280px',
            large: '1180px',
          }"
        />
      </UArticleContainer>
    </main>
  </div>
</template>

<style scoped>
@reference "~/assets/styles/main.css";

.program {
  p {
    @apply text-lg ml-4 py-[0.8rem] sm:m-0 xs:text-xl;
  }

  li {
    list-style: initial;
  }

  li:has(.time) {
    list-style: none;

    .time {
      display: grid;
      grid-template-columns: 1fr 4fr;

      @media screen and (width >= theme("screens.xs")) {
        grid-template-columns: 1fr 8fr;
      }
    }
  }
}
</style>
